import {
  createFileRoute,
  Outlet,
  redirect,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { type ReactNode, useEffect } from "react";
import { useSelector } from "react-redux";

import { AppLayout } from "~/components/layout/app-layout";
import {
  selectIsAuthenticated,
  selectIsLoadingAuth,
  selectIsOnboardingComplete,
} from "~/store";

export const Route = createFileRoute("/_app")({
  component: AppLayoutRoute,
  beforeLoad: ({ context }) => {
    // Avoid redirecting while auth state is still initializing
    if (context.authState.isLoadingAuth) return;

    if (!context.authState.isAuthenticated) {
      throw redirect({
        to: "/login",
      });
    }
  },
});

function AppLayoutRoute() {
  const isLoadingAuth = useSelector(selectIsLoadingAuth);
  const isAuthenticated = useSelector(selectIsAuthenticated);

  // Return null while loading to prevent flashing the dashboard
  if (isLoadingAuth) {
    return null;
  }

  // If not authenticated after loading, redirect to /login
  if (!isAuthenticated) {
    return <RedirectToLogin />;
  }

  return (
    <AppLayout>
      <OnboardingGuard>
        <Outlet />
      </OnboardingGuard>
    </AppLayout>
  );
}

function RedirectToLogin() {
  const navigate = useNavigate();
  // biome-ignore lint/correctness/useExhaustiveDependencies: navigate is stable from useNavigate
  useEffect(() => {
    navigate({ to: "/login", replace: true });
  }, []);
  return null;
}

// Wrapper component to check onboarding status
function OnboardingGuard({ children }: { children: ReactNode }) {
  const isOnboardingComplete = useSelector(selectIsOnboardingComplete);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const navigate = useNavigate();
  const location = useLocation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: navigate is stable from useNavigate
  useEffect(() => {
    if (!isAuthenticated) return;
    if (location.pathname === "/account/create") return;

    if (!isOnboardingComplete)
      navigate({ to: "/account/create", replace: true });
  }, [isOnboardingComplete, isAuthenticated, location.pathname]);

  return children;
}
